import React, { useState } from 'react';
import { useTranslationStore } from '../store/translationStore';
import { ChevronDownIcon, ChevronRightIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// HistoryItem type definition
interface HistoryItem {
  id: string;
  original: string;
  translation: string;
  officialTranslation: string;
  alternatives?: Array<{
    term: string;
    pinyin?: string;
    context: string;
  }>;
  pinyin?: string;
  examples: string[];
  category?: string;
  sourceLang: string;
  targetLang: string;
  searchCount: number;
  lastSearched: Date;
}

const CategoryView: React.FC = () => {
  const { categorizedHistory, deleteHistoryItem, loadHistoryItem, recategorizeHistory } = useTranslationStore();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [isRecategorizing, setIsRecategorizing] = useState(false);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleDelete = async (id: string) => {
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await deleteHistoryItem(id);
    } catch (error) {
      console.error('Failed to delete item:', error);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleHistoryItemClick = async (item: HistoryItem) => {
    setLoadingIds(prev => new Set(prev).add(item.id));
    try {
      await loadHistoryItem(item);
    } catch (error) {
      console.error('Failed to load history item:', error);
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const handleRecategorize = async () => {
    setIsRecategorizing(true);
    try {
      await recategorizeHistory();
    } catch (error) {
      console.error('Failed to recategorize:', error);
    } finally {
      setIsRecategorizing(false);
    }
  };

  const categories = Object.keys(categorizedHistory);

  if (categories.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No categorized words yet</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Categories</h2>
        <button
          onClick={handleRecategorize}
          disabled={isRecategorizing}
          className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recategorise all words into more appropriate categories using AI"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isRecategorizing ? 'animate-spin' : ''}`} />
          <span>{isRecategorizing ? 'Recategorising...' : 'Recategorise'}</span>
        </button>
      </div>

      <div className="space-y-2">
        {categories.map((category) => {
          const items = categorizedHistory[category];
          const isExpanded = expandedCategories.has(category);

          return (
            <div key={category} className="border-b border-gray-100">
              <button
                onClick={() => toggleCategory(category)}
                className="w-full py-3 hover:bg-gray-50 transition flex items-center justify-between"
              >
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 mr-2 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 mr-2 text-gray-500" />
                  )}
                  <span className="font-medium text-gray-900">{category}</span>
                  <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {items.length}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="pl-6 pb-4">
                  {items.map((item) => {
                    const isLoading = loadingIds.has(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`py-2 border-t border-gray-100 hover:bg-gray-50 cursor-pointer rounded-lg px-2 -mx-2 transition-all duration-200 ${
                          isLoading ? 'opacity-60 pointer-events-none' : ''
                        }`}
                        onClick={() => handleHistoryItemClick(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleHistoryItemClick(item);
                          }
                        }}
                        title={isLoading ? "Loading..." : "Click to load this translation"}
                      >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{item.original}</p>
                          <p className="text-sm font-medium text-gray-900">{item.translation}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            ×{item.searchCount}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            disabled={deletingIds.has(item.id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="Delete this translation"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryView;